/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from "react";
import './FilterOptions.scss';
import { Button } from "@progress/kendo-react-buttons";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { KendoCheckbox } from "components/SideBarItems/Checkbox";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { IElementType } from "components/SideBarItems/types";
import { workspaceSlice, selectSelectedTab } from "store/workspace/slice";
import { selectAllTagAttributes } from "store/sidebar/selectors";
import { trackGAEvent } from "helpers/functions/GoogleAnalytics/GA";
import { eventsGA } from "helpers/functions/GoogleAnalytics/types";
import { R_clearFilterSetSelection } from "api/sidebar/sideBarService";
import { T_changeFilterset, T_deleteFilterset, T_getFilterset, getSideBarFiltersThunk } from "store/sidebar/thunk";
import { sideBarTreeSlice } from "store/sidebar/slice";
import { IFilterSet, TreeViewDataItem } from "store/sidebar/types";
import { DropDownList, DropDownListChangeEvent, DropDownListFilterChangeEvent } from "@progress/kendo-react-dropdowns";
import { handleTreeViewCheckChange, ItemRenderProps, processTreeViewItems, TreeView, TreeViewCheckChangeEvent, TreeViewCheckDescriptor, TreeViewExpandChangeEvent } from '@progress/kendo-react-treeview';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import WithRole from "components/userRoles/withRole/WithRole";
import { selectAllTags } from "store/ccs/selectors";
import { LICENSE_TYPES } from "helpers/constants/app";
import { getWorkspaceByIdThunk, T_editWorkspaceFilters } from "store/workspace/thunk";
import { filterBy } from "@progress/kendo-data-query";
import { deepClone } from 'helpers/functions/commons';
import { Loader } from "@progress/kendo-react-indicators";
import AddFilterSetPortal from "./addFilterSetPortal";
import { filterIcon, filterClearIcon, fileDataIcon, saveIcon , trashIcon} from "@progress/kendo-svg-icons"
import { T_getBenchmarkData } from "store/explore/thunk"; 
import { selectActiveUser } from 'store/userState/selectors';
import { isReadOnlyAccess, checkAndHandleReadOnlyAccess } from 'helpers/functions/workspaceHelpers';

export const FilterOptions: React.FC<{searchValue: string}> = ({searchValue}) => {
    const dispatch = useAppDispatch();
    const { filterOptions, categorizeOptions } = useAppSelector(state => state.workspaceSlice.filters)
    const tagAttributes = useAppSelector(selectAllTagAttributes)
    const propertyCalculatorParameters = useAppSelector((state) => state.sideBarTreeSlice.propertyCalculatorParameters)
    const { workspaceId, tree, workspaceListLoading, filteredFilters, filters: {filterOptions: {filters}}, workspaces, plotId } = useAppSelector(store => store.workspaceSlice)
    const allTags = useAppSelector(selectAllTags)
    const selectedTab = useAppSelector(selectSelectedTab)
    const { filterSets: { list, loading, error, selected} } = useAppSelector(state => state.sideBarTreeSlice)
    const [data, setData] = useState<IFilterSet[]>([])
    const [showAddFilterSetPortal, setShowAddFilterSetPortal] = useState(false)
    const [loadingSearch, setLoadingSearch] = useState(false)
    const [searchLoading, setSearchLoading] = useState(false);
    
    // Check if current workspace is read-only
    const user = useAppSelector(selectActiveUser);
    const currentWorkspace = useMemo(() => workspaces.find(ws => ws.id === workspaceId) || null, [workspaces, workspaceId]);
    const isWorkspaceReadOnly = useMemo(() => isReadOnlyAccess(currentWorkspace, user?.id), [currentWorkspace, user?.id]);

    const searchTimeoutId = useRef<any>(null)

    const handleChangeValue = async (obj: { element?: IElementType, value?: unknown, group?: string | null, index?: number }): Promise<void> => {
        // Check for read-only workspace access before modifying filters
        if (checkAndHandleReadOnlyAccess(currentWorkspace, user?.id, dispatch)) {
            return;
        }
        
        // Update Redux state first for immediate UI feedback
        dispatch(workspaceSlice.actions.setFilterOptionItem(obj))
        
        switch (obj.element) {
            case 'applyToUserData':
                trackGAEvent(eventsGA.filters_applied_user_data)
                break;
            case 'applyToBenchmarkData':
                trackGAEvent(eventsGA.filters_applied_benchmark)
                // For benchmark plots, persist to backend and refresh data
                if (selectedTab?.type === 7 && selectedTab?.id && workspaceId) {
                    const foundWorkspace = workspaces.find(item => item.id === workspaceId);
                    if (foundWorkspace) {
                        try {
                            // Build updated filter options with the new value
                            const updatedFilterOptions = { 
                                ...filterOptions, 
                                applyToBenchmarkData: obj.value as boolean 
                            };
                            
                            // Update workspace filters on backend
                            await dispatch(T_editWorkspaceFilters({ 
                                id: workspaceId, 
                                plotId, 
                                title: foundWorkspace.title, 
                                unit: foundWorkspace.unit, 
                                mapOptions: tree[2], 
                                filterOptions: updatedFilterOptions, 
                                categorizeOptions 
                            }));
                            
                            // Then refresh benchmark data with updated filters
                            dispatch(T_getBenchmarkData({ workspaceId, id: selectedTab.id }));
                        } catch (error) {
                            console.error('Error updating workspace filters or benchmark data:', error);
                        }
                    }
                }
                break;
            default:
                break;
        }
    }
    const contains = (name: string | undefined, term: string) => {
        return name ? name.toLowerCase().indexOf(term.toLowerCase()) >= 0 : true;
    };
    const search = (items: TreeViewDataItem[], term: string) => {

        return items?.reduce((acc: TreeViewDataItem[], item: TreeViewDataItem) => {
            if (contains(item.name, term) && item.name) {
                acc.push(item);
            } else if (item.items && item.items.length > 0) {
                const newItems = search(item.items, term);
                if (newItems && newItems.length > 0) {
                    acc.push({
                        element: item.element,
                        type: item.type,
                        name: item.name,
                        items: newItems,
                        expanded: true,
                        id: item.id
                    });
                }
            }
            return acc;
        }, []);
    };

    const clearAllFilters = async () => {
        // Check for read-only workspace access before clearing filters
        if (checkAndHandleReadOnlyAccess(currentWorkspace, user?.id, dispatch)) {
            return;
        }
        
        // Preserve the current layer order before clearing filters
        const currentLayerOrder = tree[0]?.items?.map(item => ({
            id: item.id,
            name: item.name || '',
            element: item.element
        })) || [];

        dispatch(sideBarTreeSlice.actions.setFiltersSearchText(''))
        dispatch(workspaceSlice.actions.clearAllFilters())
        dispatch(workspaceSlice.actions.clearFiltersApplied())
        const filters = search(tree[0].items, searchValue);
        searchValue ?
            dispatch(workspaceSlice.actions.setFilteredFilters(filters)) :
            dispatch(workspaceSlice.actions.setFilteredFilters([]));
        await R_clearFilterSetSelection({workspaceId}, null)
        dispatch(sideBarTreeSlice.actions.setSelectedFilterSet(null))
        await dispatch(getSideBarFiltersThunk({ tagAttributes, propertyCalculatorParameters }));

        // Restore the layer order after filters are cleared
        if (currentLayerOrder.length > 0) {
            dispatch(workspaceSlice.actions.restoreLayerOrder(currentLayerOrder));
        }
    }

    const handleSaveValues = async (_element?: IElementType | undefined, _group?: string | null | undefined) => null;

    const handleOpenPortal = async () => {
        setShowAddFilterSetPortal(true)
    }

    const handleClosePortal = async () => {
        setShowAddFilterSetPortal(false)
    }

    const handleDelete = async (e: React.MouseEvent<HTMLSpanElement, MouseEvent>, title: string) => {
        e.stopPropagation()
        e.preventDefault()
        const id = list.find(item=>item.title === title)?.id
        if(id){
           await dispatch(T_deleteFilterset({workspaceId, filterSetId: id}))
           await dispatch(T_getFilterset({workspaceId}))
           dispatch(sideBarTreeSlice.actions.setSelectedFilterSet(null))
        }
    };

    const itemRender = (li: any) => {
        const deleteElement = (
            <Button themeColor={"primary"} onClick={(e) => handleDelete(e, li.props.children[0].props.children)}  style={{ position: "absolute", right: "5px", width:"20px", height:"20px"}} svgIcon={trashIcon}></Button>
        );
        const icon = deleteElement;
        const itemChildren = (
            <div>
                <span>{li.props.children}</span>
                {icon}
            </div>
        );

        return React.cloneElement(li, li.props, itemChildren);
    }

    const handleChange = async (e: DropDownListChangeEvent) => {
        // Check for read-only workspace access before changing filterset
        if (checkAndHandleReadOnlyAccess(currentWorkspace, user?.id, dispatch)) {
            return;
        }
        
        dispatch(sideBarTreeSlice.actions.setSelectedFilterSet(e.value))
        await dispatch(T_changeFilterset({workspaceId, filterSetId: e.value.id}))
        if(selectedTab?.id){
            await dispatch(getWorkspaceByIdThunk({id: workspaceId, allTags: allTags, tabId: selectedTab.id}))
            await dispatch(getSideBarFiltersThunk({tagAttributes: allTags, propertyCalculatorParameters}))
        }
    };

    const filterChange = (event: DropDownListFilterChangeEvent) => {
        if (!event.filter.value) {
          return setData(list)
        }
        return setData(filterBy(list, event.filter))
      }

      React.useEffect(()=>{
        const getAll = async (workspaceId: string) => {
            await dispatch(T_getFilterset({workspaceId}))
        }
        if(workspaceId){
            getAll(workspaceId)
        }
      }, [workspaceId])

      React.useEffect(()=>{
        setData(list)
      }, [list])

    
    const handleSearch = (e: InputChangeEvent) => {
        const { value } = e;
        setSearchLoading(true)
        dispatch(sideBarTreeSlice.actions.setFiltersSearchText(value))
    };

    useEffect(() => {
        if (tree[0]?.items) {
            if (searchTimeoutId.current) clearTimeout(searchTimeoutId.current)
            searchTimeoutId.current = setTimeout(() => {
                const cloned = deepClone(tree)
                const filters = search(tree[0]?.items, searchValue);
                cloned[0].items = filters
                searchValue ?
                    dispatch(workspaceSlice.actions.setFilteredFilters(cloned)) :
                    dispatch(workspaceSlice.actions.setFilteredFilters([]));
                setSearchLoading(false)
            }, 600)
        }
    }, [searchValue])

    return (
      <div className="header">
        <DropDownList
          data={data}
          itemRender={itemRender}
          loading={loading}
          textField="title"
          filterable={true}
          onFilterChange={filterChange}
          onChange={handleChange}
          value={selected || { title: "Recall Saved Filters", id: "" }}
          disabled={isWorkspaceReadOnly}
        />

        <div className={`searchFilterInput`} style={{ display: tree[0].expanded ? "block" : "block" }}>
          <Input className="k-textbox" onChange={handleSearch} placeholder="Search Filters" value={searchValue} disabled={isWorkspaceReadOnly} />
          {searchLoading && (
            <span className="searchLoader">
              <Loader />
            </span>
          )}
        </div>

        <Tooltip anchorElement="target" position="top" parentTitle={true}>
          <Button
            title={!filterOptions.isEnabled ? "Enable Filters" : "Disable Filters"}
            svgIcon={filterIcon}
            themeColor={filterOptions.isEnabled ? "primary" : "light"}
            disabled={isWorkspaceReadOnly}
            onClick={() =>
              handleChangeValue({
                element: "isEnabled",
                value: !filterOptions.isEnabled,
                group: "filterOptions",
              })
            }
          />
          <Button title="Clear All Filters" svgIcon={filterClearIcon} themeColor={"light"} disabled={isWorkspaceReadOnly} onClick={clearAllFilters} />
          <Button
            title="Apply to User Data"
            svgIcon={fileDataIcon}
            themeColor={filterOptions.applyToUserData ? "primary" : "light"}
            disabled={isWorkspaceReadOnly}
            onClick={() =>
              handleChangeValue({
                element: "applyToUserData",
                value: !filterOptions.applyToUserData,
                group: "filterOptions",
              })
            }
          />
          <Button
            title="Apply to Benchmark Data"
            svgIcon={fileDataIcon}
            themeColor={filterOptions.applyToBenchmarkData ? "primary" : "light"}
            disabled={isWorkspaceReadOnly}
            onClick={() =>
              handleChangeValue({
                element: "applyToBenchmarkData",
                value: !filterOptions.applyToBenchmarkData,
                group: "filterOptions",
              })
            }
          />
          <WithRole roles={[LICENSE_TYPES.global]}>
            <Button title="Save current filters to Filter Sets" svgIcon={saveIcon} disabled={!filters.length || workspaceListLoading || isWorkspaceReadOnly} themeColor={"light"} onClick={handleOpenPortal} />
          </WithRole>
        </Tooltip>
        <AddFilterSetPortal
          portalOpened={showAddFilterSetPortal}
          closePortal={() => {
            setShowAddFilterSetPortal(false)
          }}
        />
      </div>

      /* <KendoCheckbox
                    label="Apply to Benchmark plots"
                    element="applyToBenchmarkData"
                    group="filterOptions"
                    value={filterOptions.applyToBenchmarkData}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                />
                <KendoCheckbox
                    label="Apply to Production data"
                    element="applyToProductionData"
                    group="filterOptions"
                    value={filterOptions.applyToProductionData}
                    setFunction={handleChangeValue}
                    saveFunction={handleSaveValues}
                /> */
    )
}

